import { Link } from 'react-router-dom'
import { faEdit } from '@fortawesome/pro-light-svg-icons'
import TwinIcon from '../../../../../../baseComponents/TwinIcon'
import { getDictionaryStates } from '../../../../../../utils/globals/countries'
import { parsePhoneNumber } from '../../../../../../utils/globals/phone'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { CustomerTabKeys, useGetCustomerParsedLinkLogic } from '../../functions'
import { ComponentWithPermissions } from '../../../../../../baseComponents/CreateRoutering/types'
import { TwinDictionary } from '../../../../../../utils/globals/dictionary'
import { useMemo } from 'react'
import { getConfigParam } from '../../../../../../utils/reducers/getters'
import { CodeCustomerToShowTyInversed } from '@teinor/erp/types/company/config/parameter'

type CustomerProfileDataDashboardProps = & CustomerProfileDetailsProps & ComponentWithPermissions & { 
    title: string
}

const CustomerProfileDataDashboard: React.FC<CustomerProfileDataDashboardProps> = ({ allRowData, title }) => {
    const tab: CustomerTabKeys = 'customer'
    const { parsedLink } = useGetCustomerParsedLinkLogic({ customerType: allRowData?.customer_type, tab })
    return (
        <div className='flex flex-auto flex-col gap-10 overflow-hidden'>
            <div className='flex flex-wrap justify-between gap-8'>
                <h3>{title}</h3>
                <div className='text-green-43 mt-3 cursor-pointer'>
                    <Link to={parsedLink}>
                        <TwinIcon icon={faEdit} className='medium18' />
                    </Link>
                </div>
            </div>
            <CustomerProfileDetails allRowData={allRowData}/>
        </div>
    )
}

interface CustomerProfileDetailsProps {
    allRowData: TwinDictionary
}

const CustomerProfileDetails: React.FC<CustomerProfileDetailsProps> = ({ allRowData }: CustomerProfileDetailsProps) => {
    const { t } = useTwinTranslation()
    const fieldMemo = useMemo(() => {
        const codeCustomerToShow = getConfigParam('codeCustomerToShow')
        let fieldId: 'id' | 'code' = 'id'
        if (parseInt(String(codeCustomerToShow)) === parseInt(String(CodeCustomerToShowTyInversed['codeCustomer']))) {
            fieldId = 'code'
        }
        return fieldId
    }, [])
    const idOrCode = allRowData?.[fieldMemo] ? (allRowData[fieldMemo] + ' - ') : ''
    return (
        <div className='flex flex-col overflow-hidden'>
            <div className='flex flex-col overflow-auto gap-5'>
                <div className='mb-3 flex flex-row flex-wrap w-full justify-between gap-x-10 gap-y-2'>
                    <span className='block flex-auto medium16'>{idOrCode + (allRowData?.fiscal_name || allRowData?.name)}</span>
                    {allRowData?.id_number ?
                        <div className='flex flex-row gap-x-5 whitespace-nowrap items-center'>
                            <span className='block light16'>{t('id_number', 'DNI')}</span>
                            <span className={'block light16'}>{allRowData?.id_number}</span>
                        </div>
                        : null}
                </div>
                <div className='flex flex-col flex-auto gap-5 overflow-auto'>
                    <CustomerAddressData {...{ allRowData }} />
                    <div className='flex flex-col gap-x-8'>
                        <div className='light14 twin_elipsis'>{allRowData?.name}</div>
                        {allRowData?.email ? <span className='text-green-21'>{allRowData?.email}</span> : null}
                    </div>
                    {allRowData?.ResponsibleEmployee?.fullname_short ?
                        <div className='flex flex-col gap-x-8'>
                            <h4 className='medium16 mb-6'>{t('ourManager', 'Nuestro gestor')}</h4>
                            <div className='light14 twin_elipsis'>{allRowData?.ResponsibleEmployee?.fullname_short}</div>
                        </div>
                        : null}
                </div>
            </div>
        </div>
    )
}

interface CustomerAddressDataProps {
    allRowData: TwinDictionary
}

const CustomerAddressData: React.FC<CustomerAddressDataProps> = ({ allRowData }) => {
    let mainAdress = allRowData.CustomerAddresses?.[0] || allRowData.CustomerShops?.[0]
    if (!mainAdress) {
        return null
    }
    const states = getDictionaryStates(mainAdress?.country)
    const stateName = states[mainAdress?.state]?.name
    return (
        <div className='flex flex-col w-full gap-x-8 light14'>
            {mainAdress?.address ? <span>{mainAdress?.address + ', '}</span> : null}
            {mainAdress?.postal_code ? <span>{mainAdress?.postal_code + ', ' + stateName}</span> : null}
            <span>{parsePhoneNumber(allRowData?.phone) || parsePhoneNumber(allRowData?.mobile)}</span>
        </div>
    )
}

export default CustomerProfileDataDashboard
